import * as http from "./http";
import {
  ApiResponse,
  Customer,
  LeadDemographic,
  PaginatedApiResponse,
  ListSort,
  CivilIdDetails,
  ISalesPersonFilter,
  Phone,
  CustomerUpdateLog,
  ListCompanyContactUsers,
  AddContact,
} from "../models";
import ApiService from "./service";
import { Dict } from "../../models";

export class CustomerService extends ApiService {
  public async searchCustomer(query: string): Promise<Customer[]> {
    const url = `${this.apiDomain}/api/customer/search?query=${query}`;

    const response = await http.get<PaginatedApiResponse<Customer>>(
      url,
      this.store
    );

    return response?.data?.data;
  }

  public async findCustomer(
    query: string,
    searchType: string
  ): Promise<Customer> {
    const url = `${this.apiDomain}/api/customer/search-one?query=${query}&searchType=${searchType}`;

    const response = await http.get<ApiResponse<Customer>>(url, this.store);

    return response?.data?.data;
  }

  public async createCustomer(data: Customer) {
    const url = `${this.apiDomain}/api/customer/`;

    const response = await http.post<ApiResponse<Customer>>(
      url,
      data,
      this.store
    );

    return response?.data?.data;
  }

  public async updateDemographics(data: LeadDemographic, id?: string) {
    const url = `${this.apiDomain}/api/customer/${id}/demographics`;

    const response = await http.put<ApiResponse<Customer>>(
      url,
      data,
      this.store
    );

    return response?.data;
  }

  public async updateCustomer(data: Customer, id?: string) {
    const url = `${this.apiDomain}/api/customer/${id}/contact-details`;

    const response = await http.put<ApiResponse<Customer>>(
      url,
      data,
      this.store
    );

    return response?.data?.data;
  }
  public async mergeCustomer(
    id: string,
    mergingCustomerIds: string[],

    data: Customer
  ) {
    const url = `${this.apiDomain}/api/customer/${id}/merge-customers`;

    const response = await http.put<ApiResponse<Customer>>(
      url,
      { mergingCustomerIds, ...data },
      this.store
    );

    return response;
  }

  public async updateWorkExperiences(data: Customer, id?: string) {
    const url = `${this.apiDomain}/api/customer/${id}/work-experiences`;

    const response = await http.put<ApiResponse<Customer>>(
      url,
      data,
      this.store
    );

    return response?.data;
  }

  public async updateCustomerDocumentsDetails(data: Customer, id?: string) {
    const url = `${this.apiDomain}/api/customer/${id}/documents`;

    const response = await http.put<ApiResponse<Customer>>(
      url,
      data,
      this.store
    );

    return response?.data;
  }

  public async getCustomers(
    data: {
      page: number;
      search?: string;
      perPage: number;
      isDeal?: boolean;
      customerId?: string;
      companyId?: string;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<Customer> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/customer/all`;

    const resposne = await http.post<PaginatedApiResponse<Customer>>(
      url,
      data,
      this.store
    );
    return { ...resposne.data, lastUpdatedAt: new Date() };
  }

  public async getCustomersByAccess(
    data: {
      isDeal?: boolean;
      customerId?: string;
    } & ListSort &
      Dict
  ): Promise<ApiResponse<{ customer: Customer; lastAccessedDate: Date }[]>> {
    const url = `${this.apiDomain}/api/customer/by-access`;

    const resposne = await http.post<
      ApiResponse<{ customer: Customer; lastAccessedDate: Date }[]>
    >(url, data, this.store);
    return resposne.data;
  }

  public async getDetails(id: string): Promise<Customer> {
    const url = `${this.apiDomain}/api/customer/${id}`;
    const response = await http.get<Customer>(url, this.store);

    return response?.data;
  }
  public async updateCivilIdDetails(
    civilIdDetails: CivilIdDetails,
    id?: string
  ) {
    const url = `${this.apiDomain}/api/customer/${id}/civilid-details`;

    const response = await http.put<ApiResponse<Customer>>(
      url,
      civilIdDetails,
      this.store
    );

    return response?.data;
  }
  public async downloadCustomers(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
      isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/customer/list/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      "application/application/vnd.openxmlformats",
      `Customers-${new Date()}.xlsx`
    );
  }

  public async searchSalesPerson(
    filter?: ISalesPersonFilter,
    listName?: string,
    companyId?: string
  ) {
    let data = {};
    if (filter) {
      data = { ...filter, companyId };
    }

    const url = `${this.apiDomain}/api/user/sales-persons/list`;
    if (listName) {
      data = { ...data, list: listName };
    }

    const response = await http.post<ApiResponse<Customer[]>>(
      url,
      data,
      this.store
    );

    return response?.data?.data;
  }

  public async searchLeadAssigneeHead(
    filter?: ISalesPersonFilter,
    listName?: string,
    companyId?: string
  ) {
    let data = {};
    if (filter) {
      data = { ...filter, companyId };
    }

    const url = `${this.apiDomain}/api/user/city-heads/list`;
    if (listName) {
      data = { ...data, list: listName };
    }

    const response = await http.post<ApiResponse<Customer[]>>(
      url,
      data,
      this.store
    );

    return response?.data?.data;
  }

  public async searchAssignee(
    filter?: ISalesPersonFilter,
    listName?: string,
    companyId?: string
  ) {
    let data = {};
    if (filter) {
      data = { ...filter, companyId };
    }

    const url = `${this.apiDomain}/api/user/assignees/list`;
    if (listName) {
      data = { ...data, list: listName };
    }

    const response = await http.post<ApiResponse<Customer[]>>(
      url,
      data,
      this.store
    );

    return response?.data?.data;
  }

  public async searchCrmAgents(
    filter?: ISalesPersonFilter,
    listName?: string,
    companyId?: string
  ) {
    let data = {};
    if (filter) {
      data = { ...filter, companyId };
    }

    const url = `${this.apiDomain}/api/user/crm-agents/list`;
    if (listName) {
      data = { ...data, list: listName };
    }

    const response = await http.post<ApiResponse<Customer[]>>(
      url,
      data,
      this.store
    );

    return response?.data?.data;
  }

  public async searchStaff(
    filter?: ISalesPersonFilter,
    listName?: string,
    companyId?: string
  ) {
    let data = {};
    if (filter) {
      data = { ...filter, companyId };
    }

    const url = `${this.apiDomain}/api/user/staffs/list?hideBlocked=true`;
    if (listName) {
      data = { ...data, list: listName };
    }

    const response = await http.post<ApiResponse<Customer[]>>(
      url,
      data,
      this.store
    );

    return response?.data?.data;
  }

  public async getCompanyPeople(companyid: string) {
    const url = `${this.apiDomain}/api/company/peoples/${companyid}`;
    const response = await http.get<ApiResponse<Customer[]>>(url, this.store);

    return response?.data?.data;
  }

  public async unlinkPhoneSendSms(phone: string, id?: string) {
    const url = `${this.apiDomain}/api/customer/${id}/unlink-phone/send-sms`;

    const response = await http.post<ApiResponse<Customer>>(
      url,
      { phone },
      this.store
    );

    return response?.data;
  }

  public async unlinkPhoneVerify(
    data: { phone: string; otp: string },
    id?: string
  ) {
    const url = `${this.apiDomain}/api/customer/${id}/unlink-phone/verify`;

    const response = await http.post<ApiResponse<Phone>>(url, data, this.store);
    return response?.data;
  }

  public async getCustomerAddresses(id: string) {
    const url = `${this.apiDomain}/api/customer/${id}/address`;

    const response = await http.get<ApiResponse>(url, this.store);

    return response?.data;
  }

  public async getUserProfilePhoto(id?: string): Promise<string> {
    const url = `${this.apiDomain}/api/customer/${id}/profile-photo`;

    const resposne = await http.get<ApiResponse<string>>(url, this.store);

    return resposne?.data?.data;
  }

  public async downloadEmailReport(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
      isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/notification/email-report/download`;

    const response = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      response.data,
      "application/application/vnd.openxmlformats",
      `Report-${new Date()}.xlsx`
    );
  }

  // lead-transfer-report
  public async CustomerDataChangeReport(
    data: {
      page: number;
      search?: string;
      perPage: number;
      isDeal?: boolean;
      customerId?: string;
      applyVisibilityFilter?: boolean;
    } & ListSort &
      Dict
  ): Promise<
    PaginatedApiResponse<CustomerUpdateLog> & { lastUpdatedAt: Date }
  > {
    const url = `${this.apiDomain}/api/customer/customer-data-change-report`;

    const resposne = await http.post<PaginatedApiResponse<CustomerUpdateLog>>(
      url,
      data,
      this.store
    );
    return { ...resposne.data, lastUpdatedAt: new Date() };
  }

  public async downloadCustomerDataChangeReport(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
      isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/customer/customer-data-change-report/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      "application/application/vnd.openxmlformats",
      `CustomerDataChangeReport-${new Date()}.xlsx`
    );
  }

  public async createCompanyCustomer(data: Customer) {
    const url = `${this.apiDomain}/api/customer/company-user`;

    const response = await http.post<ApiResponse<Customer>>(
      url,
      data,
      this.store
    );

    return response?.data?.data;
  }

  public async listCompanyContactUsers(
    data: ListCompanyContactUsers & {
      page: number;
      search?: string;
      perPage: number;
    } & ListSort &
      Dict
  ) {
    const url = `${this.apiDomain}/api/customer/list-company`;

    const response = await http.post<PaginatedApiResponse<Customer>>(
      url,
      data,
      this.store
    );

    return response?.data?.data;
  }

  public async listCustomersByProperty(propertyId: string) {
    const url = `${this.apiDomain}/api/customer/by-property`;

    const response = await http.post<ApiResponse<Customer[]>>(
      url,
      { propertyId },
      this.store
    );

    return response?.data?.data;
  }

  public async addExistingCustomerAsCompanyContact(data: AddContact) {
    const url = `${this.apiDomain}/api/customer/add/contact`;

    const response = await http.put<ApiResponse<Customer>>(
      url,
      data,
      this.store
    );

    return response?.data;
  }

  public async listCorporateCustomers(
    data: {
      page: number;
      search?: string;
      perPage: number;
      companyId?: string;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<Customer> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/customer/list-corporate`;

    const resposne = await http.post<PaginatedApiResponse<Customer>>(
      url,
      data,
      this.store
    );
    return { ...resposne.data, lastUpdatedAt: new Date() };
  }

  public async maskContact(id?: string, data?: any) {
    const url = `${this.apiDomain}/api/customer/${id}/mask-contact`;

    const response = await http.put<ApiResponse<any>>(url, data, this.store);
    return response?.data;
  }

  public async unMaskContact(id?: string, data?: any) {
    const url = `${this.apiDomain}/api/customer/${id}/unmask-contact`;

    const response = await http.put<ApiResponse<any>>(url, data, this.store);
    return response?.data;
  }

  public async viewMaskedContact(id: string, contactType: string) {
    const url = `${this.apiDomain}/api/customer/${id}/view-masked-contact`;
    const response = await http.post<any>(url, { contactType }, this.store);

    return response?.data;
  }
}
